import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import FilterSidebar from './FilterSidebar';
import GPUBrowserHeader from './GPUBrowserHeader';
import GPUBrowserTable from './GPUBrowserTable';
import { sortCardSeries } from './gpuUtils';

const GPUBrowser = () => {
    const [gpus, setGpus] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Filter states
    const [selectedManufacturers, setSelectedManufacturers] = useState([]);
    const [selectedCardSeries, setSelectedCardSeries] = useState([]);
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [priceRange, setPriceRange] = useState([0, 5000]); // Default values
    // Sorting state
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    // New search state
    const [searchTerm, setSearchTerm] = useState("");
    // Mobile filter toggle state
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const { data, error } = await supabase
                    .from('products')
                    .select('id, title, cardseries, manufacturer, brand, current_price, product_url, price_history, last_updated, base_stock, best_used_price, best_used_condition');
                if (error) {
                    setError(error.message);
                } else {
                    setGpus(data);
                }
            } catch (err) {
                setError("An unexpected error occurred");
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const computedMaxPrice = gpus.length
        ? Math.ceil(Math.max(...gpus.filter(gpu => gpu.current_price !== null).map(gpu => gpu.current_price)) / 50) * 50
        : 5000;

    useEffect(() => {
        if (gpus.length > 0 && priceRange[1] === 5000) {
            setPriceRange([0, computedMaxPrice]);
        }
    }, [gpus, computedMaxPrice, priceRange]);

    const lastUpdated = gpus.length > 0
        ? new Date(Math.max(...gpus.map(gpu => new Date(gpu.last_updated).getTime())))
        : null;

    const uniqueManufacturers = [...new Set(gpus.map(gpu => gpu.manufacturer).filter(Boolean))];

    let availableCardSeries = selectedManufacturers.length > 0
        ? [
            ...new Set(
                gpus
                    .filter(gpu => selectedManufacturers.includes(gpu.manufacturer))
                    .map(gpu => gpu.cardseries)
                    .filter(Boolean)
            )
        ]
        : [...new Set(gpus.map(gpu => gpu.cardseries).filter(Boolean))];

    // Sort card series using our helper, which now handles both RTX and RX cards.
    availableCardSeries = availableCardSeries.sort(sortCardSeries);

    const availableBrands = selectedManufacturers.length || selectedCardSeries.length
        ? [
            ...new Set(
                gpus
                    .filter(gpu => {
                        if (selectedManufacturers.length && !selectedManufacturers.includes(gpu.manufacturer)) return false;
                        if (selectedCardSeries.length && !selectedCardSeries.includes(gpu.cardseries)) return false;
                        return true;
                    })
                    .map(gpu => gpu.brand)
                    .filter(Boolean)
            )
        ]
        : [...new Set(gpus.map(gpu => gpu.brand).filter(Boolean))];

    const handleCheckboxChange = (category, value) => {
        const updater = {
            manufacturer: setSelectedManufacturers,
            cardseries: setSelectedCardSeries,
            brand: setSelectedBrands,
        }[category];
        updater(prev =>
            prev.includes(value) ? prev.filter(item => item !== value) : [...prev, value]
        );
    };

    if (loading)
        return <div className="text-center text-lg font-semibold mt-10">Loading...</div>;
    if (error)
        return <div className="text-center text-red-500 font-semibold mt-10">Error: {error}</div>;

    return (
        <div className="max-w-screen-2xl mx-auto p-2">
            {lastUpdated && (
                <div className="text-sm text-gray-600 mb-2">
                    Last updated on {lastUpdated.toLocaleString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        hour12: true,
                        timeZoneName: 'short'
                    })}
                </div>
            )}

            <GPUBrowserHeader title="GPU Browser" onToggleFilters={() => setShowFilters(true)} />
            <div className="lg:flex lg:gap-6">
                <div className="hidden lg:block lg:w-1/4">
                    <FilterSidebar
                        uniqueManufacturers={uniqueManufacturers}
                        availableCardSeries={availableCardSeries}
                        availableBrands={availableBrands}
                        selectedManufacturers={selectedManufacturers}
                        selectedCardSeries={selectedCardSeries}
                        selectedBrands={selectedBrands}
                        handleCheckboxChange={handleCheckboxChange}
                        priceRange={priceRange}
                        setPriceRange={setPriceRange}
                        computedMaxPrice={computedMaxPrice}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                    />
                </div>
                <main className="w-full h-auto overflow-y-auto">
                    <GPUBrowserTable
                        gpus={gpus}
                        sortConfig={sortConfig}
                        setSortConfig={setSortConfig}
                        searchTerm={searchTerm}
                        selectedManufacturers={selectedManufacturers}
                        selectedCardSeries={selectedCardSeries}
                        selectedBrands={selectedBrands}
                        priceRange={priceRange}
                    />
                </main>
            </div>
            {showFilters && (
                <div className="fixed inset-0 z-50 bg-white p-2 overflow-y-auto lg:hidden">
                    <FilterSidebar
                        uniqueManufacturers={uniqueManufacturers}
                        availableCardSeries={availableCardSeries}
                        availableBrands={availableBrands}
                        selectedManufacturers={selectedManufacturers}
                        selectedCardSeries={selectedCardSeries}
                        selectedBrands={selectedBrands}
                        handleCheckboxChange={handleCheckboxChange}
                        priceRange={priceRange}
                        setPriceRange={setPriceRange}
                        computedMaxPrice={computedMaxPrice}
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                    />
                    <button onClick={() => setShowFilters(false)} className="mt-4 p-2 border rounded">
                        Close
                    </button>
                </div>
            )}
            <footer className="mt-6 text-center text-xs text-gray-500">
                Disclaimer: This site is an Amazon Associate and may earn commissions from qualifying purchases. Final pricing and product availability are confirmed at the time of purchase. All information provided is for reference only.
            </footer>
        </div>
    );
};

export default GPUBrowser;
